export const NATIVE_BARCODE_FORMATS = ["ean_13", "ean_8", "upc_a", "upc_e"] as const;
export const PRICE_TAG_BARCODE_FORMATS = ["ean_13", "ean_8", "upc_a", "upc_e", "qr_code", "code_128"] as const;

export interface NativeBarcodeDetector {
  detect(source: CanvasImageSource): Promise<Array<{ rawValue?: string; format?: string }>>;
}

export interface ViewfinderCrop {
  widthRatio: number;
  heightRatio: number;
}

/** Matches `.viewfinder__frame` (~74vw × 48vw). */
export const BARCODE_CROP: ViewfinderCrop = { widthRatio: 0.74, heightRatio: 0.48 };
/** Wider, taller crop for vertical ESL / paper shelf tags. */
export const PRICE_TAG_CROP: ViewfinderCrop = { widthRatio: 0.9, heightRatio: 0.7 };

export function normalizeDetectedValue(raw: string | null | undefined): string | null {
  if (!raw || typeof raw !== "string") return null;
  const cleaned = raw.replace(/[\s-]/g, "");
  return cleaned.length > 0 ? cleaned : null;
}

export function createNativeBarcodeDetector(
  formats: readonly string[] = NATIVE_BARCODE_FORMATS,
): NativeBarcodeDetector | null {
  const Ctor =
    (typeof window !== "undefined" &&
      (window as unknown as { BarcodeDetector?: new (opts?: { formats?: string[] }) => NativeBarcodeDetector })
        .BarcodeDetector) ||
    null;
  if (!Ctor) return null;
  try {
    return new Ctor({ formats: [...formats] });
  } catch {
    try {
      return new Ctor();
    } catch {
      return null;
    }
  }
}

/** Center crop matching the on-screen viewfinder. */
export function cropVideoToViewfinder(
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement = document.createElement("canvas"),
  crop: ViewfinderCrop = BARCODE_CROP,
): HTMLCanvasElement {
  const vw = video.videoWidth || 0;
  const vh = video.videoHeight || 0;
  if (vw < 8 || vh < 8) {
    canvas.width = 0;
    canvas.height = 0;
    return canvas;
  }
  const cw = Math.max(8, Math.round(vw * crop.widthRatio));
  const ch = Math.max(8, Math.round(vh * crop.heightRatio));
  const sx = Math.round((vw - cw) / 2);
  const sy = Math.round((vh - ch) / 2);
  canvas.width = cw;
  canvas.height = ch;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx?.drawImage(video, sx, sy, cw, ch, 0, 0, cw, ch);
  return canvas;
}

export async function detectFromNative(
  source: CanvasImageSource,
  detector: NativeBarcodeDetector | null,
): Promise<string[]> {
  if (!detector) return [];
  try {
    const codes = await detector.detect(source);
    const values: string[] = [];
    for (const code of codes) {
      const normalized = normalizeDetectedValue(code.rawValue);
      if (normalized) values.push(normalized);
    }
    return values;
  } catch {
    return [];
  }
}
